import type { IUser } from '@manager/api/types/auth-types';
import { useCan } from '@manager/composables/use-can';
import { useAuthStore } from '@manager/store/auth-store';
import type { IPermission } from '@manager/types/permission-types';
import { isObject, kebabCase } from 'lodash-es';
import type { Component } from 'vue';
import { useRouter } from 'vue-router';

import { type IManagerFeature, useManagerFeature } from './use-manager-feature';

export interface IMenuItem {
  id: string;
  title: string;
  path: string;
  icon: string;
  permission: IPermission | IPermission[];
  feature?: IManagerFeature;
  additionalCheck?: (user: IUser) => boolean;

  props: {
    to: string;
    color: string;
    link: boolean;
    dataTestId: string;
    prependIcon: string;
  };
}

export interface IMenuSection {
  title: string;
  permission?: IPermission | IPermission[];
  items: (IMenuItem | IMenuGroup)[];
}

export interface IMenuGroup {
  id: string;
  title: string;
  icon: string;
  permission?: IPermission | IPermission[];
  items: IMenuItem[];
}

export function isMenuGroup(item: unknown): item is IMenuGroup {
  return (item as IMenuGroup).items != null;
}

export function isComponentIcon(icon: string | Component): icon is Component {
  return isObject(icon);
}

export function useNavigationBuilder() {
  const { canSome } = useCan();
  const router = useRouter();
  const authStore = useAuthStore();

  const { isFeatureDisabled } = useManagerFeature();

  // Keep menu IDs stable so Vue preserves v-list-group open state across nested route changes.
  function stableId(...parts: (string | undefined)[]) {
    const id = kebabCase(parts.filter(Boolean).join('-'));

    return id || 'item';
  }

  function getItem(routeName: string, icon: string | Component) {
    const routeData = router.resolve({ name: routeName });
    const path = routeData.path;
    const name = routeData.name as string;
    const meta = routeData.meta;

    const result = {
      id: `menu-item-${stableId(name || path)}`,
      title: `default.menu.${name || ''}`,
      path: path,
      icon: icon,
      permission: meta?.permission,
      feature: meta?.feature,
      additionalCheck: meta?.additionalCheck,
    };

    return {
      ...result,

      props: {
        to: result.path,
        color: 'primary',
        link: true,
        dataTestId: 'main-menu-item',
        prependIcon: icon,
      },
    } as IMenuItem;
  }

  function isItemVisible(item: IMenuItem) {
    if (!canSome(item.permission) || isFeatureDisabled(item.feature)) {
      return false;
    }

    if (!item.additionalCheck) {
      return true;
    }

    return !!authStore.user && item.additionalCheck(authStore.user);
  }

  function getMenu(config: IMenuSection[]): (IMenuSection & { id: string })[] {
    const validSections: IMenuSection[] = [];

    for (const sectionOriginal of config) {
      const section: IMenuSection = {
        ...sectionOriginal,
        items: [],
      };
      const children = sectionOriginal.items;

      if (children.length <= 0 || !canSome(sectionOriginal.permission)) {
        continue;
      }

      for (const childGroup of children) {
        if (!canSome(childGroup.permission)) {
          // skipping if none of the given permissions is allowed
          continue;
        }

        if (isMenuGroup(childGroup)) {
          // Filtering out not allowed children
          const groupCopy: IMenuGroup = {
            ...childGroup,
            items: childGroup.items.filter(isItemVisible),
          };

          // Only add group if it contains children, else skip it
          if (groupCopy.items.length) {
            section.items.push(groupCopy);
          }
        } else {
          // If it is just a normal menu item, add it:
          if (isItemVisible(childGroup)) {
            section.items.push(childGroup);
          }
        }
      }

      // If the section contains valid items, add it
      if (section.items.length) {
        validSections.push(section);
      }
    }

    return validSections.map((s) => ({
      ...s,
      id: `menu-section-${stableId(s.title)}`,
    }));
  }

  function getGroup(options: {
    title: string;
    icon: string;
    items: IMenuItem[];
  }): IMenuGroup {
    const itemIds = options.items.map((item) => item.id).join('-');

    return {
      id: `menu-group-${stableId(options.title, itemIds)}`,
      title: options.title,
      icon: options.icon,
      items: options.items,
    };
  }

  return {
    getItem,
    getMenu,
    getGroup,
  };
}
